// v2/pages/ProductsV2.js — urunler sayfasi (sunucu tarafli, filtreler URL'de)
// URL: ?tab=all|frequent|campaign &brand= &model= &segment= &engine=
//      &category= &sub= &stock= &campaign= &q= &sort= &per= &page=
import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductTable from '../components/ProductTable';
import CatalogFilters from '../components/CatalogFilters';
import ProductDetailModal from '../components/ProductDetailModal';
import SearchNotice from '../components/SearchNotice';
import ToastStack from '../components/Toast';
import useToast from '../hooks/useToast';
import useMenuNav from '../hooks/useMenuNav';
import useCartContext from '../hooks/CartContext';
import useExchangeRates from '../hooks/useExchangeRates';
import { useCatalogProducts, useCatalogFacets } from '../hooks/useCatalog';
import { PAGE_SIZE_OPTIONS } from '../constants/theme';
import styles from '../styles/DashboardV2.module.css';
import '../styles/tokens.css';

const TABS = ['all', 'frequent', 'campaign'];
const KAMPANYA = ['all', 'yes', 'general'];
const STOK = ['all', 'in', 'gzt', 'ist', 'out'];

const BOS = {
  tab: 'all', campaign: 'all', stock: 'all', search: '',
  brand: '', model: '', segment: '', engine: '', category: '', sub: '',
  sort: '', perPage: PAGE_SIZE_OPTIONS[0], page: 1,
};

// URL anahtari -> filtre alani
const URL_ALAN = {
  tab: 'tab', brand: 'brand', model: 'model', segment: 'segment', engine: 'engine',
  category: 'category', sub: 'sub', stock: 'stock', campaign: 'campaign',
  q: 'search', sort: 'sort', per: 'perPage', page: 'page',
};

function duzelt(f) {
  const n = { ...f };
  if (!TABS.includes(n.tab)) n.tab = 'all';
  if (!KAMPANYA.includes(n.campaign)) n.campaign = 'all';
  if (!STOK.includes(n.stock)) n.stock = 'all';
  n.page = Math.max(parseInt(n.page, 10) || 1, 1);
  n.perPage = PAGE_SIZE_OPTIONS.includes(Number(n.perPage)) ? Number(n.perPage) : BOS.perPage;
  if (n.tab === 'campaign') n.campaign = 'general';
  if (!n.brand) n.model = '';
  if (!n.category) n.sub = '';
  return n;
}

function urldenOku(sp) {
  const f = { ...BOS };
  Object.entries(URL_ALAN).forEach(([k, alan]) => {
    const v = sp.get(k);
    if (v !== null) f[alan] = v;
  });
  // Ana sayfadaki kampanya butonu ?campaign=general ile gelir
  if (f.campaign === 'general' && (!f.tab || f.tab === 'all')) f.tab = 'campaign';
  return duzelt(f);
}

function urleYaz(f) {
  const p = new URLSearchParams();
  Object.entries(URL_ALAN).forEach(([k, alan]) => {
    const v = f[alan];
    if (v === '' || v === null || v === undefined || v === BOS[alan]) return;
    if (k === 'campaign' && f.tab === 'campaign') return;   // sekmeden anlasiliyor
    p.set(k, String(v));
  });
  return p.toString();
}

export default function ProductsV2() {
  const [sp, setSp] = useSearchParams();
  const [f, setF] = useState(() => urldenOku(sp));
  const [tabloAnahtari, setTabloAnahtari] = useState(0);
  const [detailProduct, setDetailProduct] = useState(null);

  const cart = useCartContext();
  const toast = useToast();
  const kurlar = useExchangeRates();
  const liste = useCatalogProducts(f);
  const secenek = useCatalogFacets(f);
  const handleMenu = useMenuNav(toast.push);

  // filtre -> URL (gecmise kayit eklemeden)
  useEffect(() => {
    const yeni = urleYaz(f);
    if (yeni !== sp.toString()) setSp(new URLSearchParams(yeni), { replace: true });
  }, [f, sp, setSp]);

  const guncelle = useCallback((yama, tabloyuYenile = false) => {
    setF((o) => {
      const n = { ...o, ...yama };
      if (!('page' in yama)) n.page = 1;
      if ('tab' in yama) {
        if (yama.tab !== 'campaign' && o.tab === 'campaign') n.campaign = 'all';
      } else if ('campaign' in yama) {
        if (yama.campaign === 'general' && n.tab === 'all') n.tab = 'campaign';
        else if (yama.campaign !== 'general' && n.tab === 'campaign') n.tab = 'all';
      }
      return duzelt(n);
    });
    if (tabloyuYenile) setTabloAnahtari((k) => k + 1);
  }, []);

  // ProductTable: search / stock / campaign / sort / page / perPage
  const tabloDegisti = useCallback((yama) => guncelle(yama), [guncelle]);
  // Sekme degisince tablo arac cubugu (kampanya secimi) yeniden kurulur
  const filtreDegisti = useCallback((yama) => guncelle(yama, 'tab' in yama), [guncelle]);
  const aracKategoriTemizle = useCallback(() => {
    guncelle({ brand: '', model: '', segment: '', engine: '', category: '', sub: '' });
  }, [guncelle]);
  const aramaDegisti = useCallback((deger) => guncelle({ search: deger }), [guncelle]);

  const handleAddToCart = useCallback((product, qty, wh) => {
    cart.add(product, qty, wh);
    toast.push(`${product.description} — ${qty} adet ${wh ? wh.label : 'sepete'} eklendi`);
  }, [cart, toast]);

  const eurRate = kurlar.eurRate || liste.eurRate;

  return (
    <div className={styles.page}>
      <ToastStack items={toast.items} onDismiss={toast.dismiss} />
      <Header
        searchTerm={f.search}
        onSearchChange={aramaDegisti}
        suggest={false}
        cart={cart}
        eurRate={eurRate}
        onMenuSelect={handleMenu}
      />

      <main className={styles.main}>
        <CatalogFilters
          filters={f}
          facets={secenek.data}
          total={liste.totalItems}
          loading={secenek.loading}
          onChange={filtreDegisti}
          onClearAll={aracKategoriTemizle}
        />

        {liste.error && (
          <div role="alert" style={{
            marginTop: 12, padding: '10px 14px', borderRadius: 10, fontSize: 13,
            color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA',
          }}>
            {liste.error}
          </div>
        )}

        <SearchNotice
          info={liste.aramaBilgi}
          current={f.search}
          onClear={() => guncelle({ search: '' }, true)}
        />

        <div style={{ opacity: liste.refreshing ? 0.55 : 1, transition: 'opacity 150ms' }}>
          <ProductTable
            key={tabloAnahtari}
            products={liste.products}
            onAddToCart={handleAddToCart}
            onOpenDetail={setDetailProduct}
            eurRate={eurRate}
            loading={liste.loading}
            quickOnClick
            initialPageSize={f.perPage}
            initialCampaign={f.campaign}
            initialStock={f.stock}
            initialSearch={f.search}
            serverMode
            serverPage={f.page}
            serverTotalPages={liste.totalPages}
            serverTotalItems={liste.totalItems}
            onServerChange={tabloDegisti}
          />
        </div>
      </main>
      <Footer />

      {detailProduct && (
        <ProductDetailModal
          product={detailProduct}
          onClose={() => setDetailProduct(null)}
          onAddToCart={handleAddToCart}
          eurRate={eurRate}
        />
      )}
    </div>
  );
}
